import React, { ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

/** Lado preferido. Si ahí no cabe, Popper elige otro por su cuenta. */
export type TipLado = 'auto' | 'top' | 'bottom' | 'left' | 'right';

/**
 * Dos clases de globo, que se distinguen a simple vista:
 *
 * - `nombre`: cómo se llama un botón que solo tiene icono. Oscuro, pequeño y
 *   de una línea; se lee de reojo y se olvida.
 * - `ayuda`: explica algo del campo o de la columna. Claro, más ancho y con
 *   varias líneas; se lee con calma.
 * - `menu`: como `nombre`, pero en el azul del menú lateral, para que no
 *   parezca de otra parte de la aplicación.
 */
export type TipTipo = 'nombre' | 'ayuda' | 'menu';

interface TipProps {
  /** Identificador del globo; lo necesita el lector de pantalla. */
  id: string;
  /** Lo que dice. Vacío o nulo: no se muestra nada y el hijo va tal cual. */
  label: ReactNode;
  placement?: TipLado;
  /** Por omisión `ayuda`: el texto explicativo es el caso más común. */
  tipo?: TipTipo;
  children: ReactElement;
}

/**
 * Ajustes de Popper, iguales para todos los globos:
 *
 * - `preventOverflow` lo empuja hacia dentro cuando queda pegado a un borde.
 * - `flip` lo manda al lado opuesto si en el preferido se saldría de la
 *   pantalla. Sin esto, el del botón de cerrar sesión —en la esquina— salía
 *   cortado o fuera de la ventana.
 */
const MODIFICADORES = [
  { name: 'preventOverflow', options: { padding: 8 } },
  {
    name: 'flip',
    options: { fallbackPlacements: ['bottom', 'top', 'left', 'right'], padding: 8 },
  },
];

/**
 * Cuánto dura a la vista el globo que solo nombra un botón.
 *
 * Pasado ese rato ya cumplió: quien lo leyó no necesita verlo mientras siga
 * apuntando, y el globo tapa lo que hay debajo. El de ayuda no se va solo: ese
 * se lee con calma y a veces son varias líneas.
 */
const DURACION_NOMBRE_MS = 5000;

/**
 * Globo de la aplicación, en lugar del `title` del navegador, que tarda en
 * salir y no admite estilo. Se dibuja fuera de su contenedor para que no lo
 * recorte una tabla ni el menú lateral.
 */
export const Tip = ({ id, label, placement = 'auto', tipo = 'ayuda', children }: TipProps) => {
  const [visible, setVisible] = useState(false);
  const temporizador = useRef<number>();

  useEffect(() => () => window.clearTimeout(temporizador.current), []);

  const alAlternar = (siguiente: boolean) => {
    window.clearTimeout(temporizador.current);
    setVisible(siguiente);
    if (siguiente && tipo !== 'ayuda') {
      temporizador.current = window.setTimeout(() => setVisible(false), DURACION_NOMBRE_MS);
    }
  };

  if (label === null || label === undefined || label === '') {
    return children;
  }
  return (
    <OverlayTrigger
      placement={placement}
      flip
      show={visible}
      onToggle={alAlternar}
      container={document.body}
      popperConfig={{ modifiers: MODIFICADORES }}
      overlay={
        <Tooltip id={id} className={`ui-tip ui-tip--${tipo}`}>
          {label}
        </Tooltip>
      }
    >
      {children}
    </OverlayTrigger>
  );
};

export default Tip;
